import Pagination from "components/_common/NumberedPaginationCentered";
import React, { FC, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux';
import { Dispatch } from 'redux';
import { historyActions } from 'redux/_core/history';
import { IStore } from 'redux/store';
import { rootRoutes } from 'routes';
import { logActions, logSelectors } from "redux/app/logs";
import TabNavigation from '../../_elements/TabNavigation';
import ReportTableExpanded from './ReportTableExpanded';
import Select from "react-select";
import { IOption } from "redux/app/feedback/@types";
import ModalPortal from "components/app/_common/ModalPortal";
import CustomizeColumns from "./_elements/CustomizeColumns";
import { allCNRColumns, cnrTabs, vesselSubTypeMap } from "redux/app/vessel/onboard/constants";
import { vesselOnboardActions, vesselOnboardSelectors } from "redux/app/vessel/onboard";
import InfiniteProgress from "components/_common/InfiniteProgress";
import StatusModal from "./_elements/StatusModal";
import { IReportErrorDetails } from "../Reports";
import { EReportType } from "redux/app/logs/@types";
import classNames from "classnames";
import { getStoredColumns } from "redux/app/vessel/onboard/utils";

type ReportsOwnProps = {
	pageNumber: string;
	vesselId: string;
	isExpandedView: boolean;
};

type ReportsProps = PropsFromRedux & ReportsOwnProps;

export const selectInputStyles = "pl-1 py-0.5";
const Reports: FC<ReportsProps> = (props) => {
	const {
		reports,
		paginationOfReports,
		pageNumber,
		jobToLoadReports,
		jobToFilterReports,
		vesselId,
		selectedColumns,
		vesselSubType,
		vesselFuelType,
		isExpandedView,
	} = props;

	const [activeTab, setActiveTab] = useState<string>("noonReport");
	const [filter, setFilter] = useState<IOption>({
		value: "",
		label: "",
	});
	const [customizeMenuOpen, setCustomizeMenuOpen] = useState<boolean>(false);
	const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
	const [errorDetails, setErrorDetails] = useState<IReportErrorDetails | null>(null);

	const vesselGenereicSubType = vesselSubTypeMap[vesselSubType];

	useEffect(() => {
		// INFO: when user selects a new vessel in the list will run again
		if (!isExpandedView) return;
		props.loadExpandedReports(
			+pageNumber,
			true,
			vesselId || "",
			filter.value
				? [filter.value]
				: cnrTabs.find(tab => tab.id === activeTab)?.type || []
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselId, pageNumber, activeTab, filter.value]);

	useEffect(() => {
		// Load custom columns data from local storage
		const parsedValues = getStoredColumns();
		props.setCustomColumns(parsedValues);

		return () => {
			props.resetPageNumber();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	return (
		<>
			<InfiniteProgress
				isLoading={
					jobToLoadReports.isLoading ||
					jobToFilterReports.isLoading
				}
				isSpacedOut={false}
			/>
			<div className="min-h-full py-6 pt-0 mb-6 bg-white rounded-md">
				<div className="mb-6">
					<div className='border border-gray-300 rounded-lg'>
						<div className='flex flex-col lg:flex-row gap-2 items-start lg:gap-0 lg:items-end justify-between py-2 px-2 bg-white rounded-lg rounded-b-none w-full'>
							<TabNavigation
								tabs={cnrTabs}
								activeTab={activeTab}
								setActiveTab={(tabId) => {
									setActiveTab(tabId);

									// Reset page number and filter on tab change
									props.resetPageNumber();
									props.goToNewPage(
										vesselId || "",
										1
									);
									setFilter({ value: "", label: "" });
								}}
								vesselId={vesselId}
								className='w-1/2 border-b border-gray-300'
							/>

							<div className='flex items-center gap-1 lg:py-1 md:gap-4 lg:border-b border-gray-300'>
								{activeTab === "noonReport" &&
									<Select
										id="device-type-id"
										options={[{ value: '', label: "All Reports" }, { value: "STEAMING", label: "Steaming" }, { value: "PORT", label: "Port" }, { value: "ARRIVAL", label: "Arrival" }, { value: "DEPARTURE", label: "Departure" }]}
										placeholder={
											<div className="flex gap-2 items-center">
												<span className="bp3-icon bp3-icon-filter" />
												<span className="w-full whitespace-no-wrap bp3-text-overflow-ellipsis">
													All Reports
												</span>
											</div>
										}
										isClearable={!!filter.value}
										isDisabled={jobToLoadReports.isLoading}
										isSearchable={false}
										unstyled
										value={filter.value ? filter : null}
										onChange={(value) => {
											if (value) {
												setFilter(value);
											} else {
												setFilter({ value: "", label: "" })
											}
										}}
										classNames={{
											control: (state) => `border border-gray-300 px-2 rounded-lg w-32 md:w-40 h-10 text-[12px] xl:text-sm ${state.isDisabled && 'bg-gray-100 cursor-not-allowed opacity-50'}`,
											option: () => 'text-gray-900 hover:bg-gray-100 cursor-pointer p-2 text-[12px] xl:text-sm',
											menu: () => 'rounded-lg bg-white border border-gray-300 shadow-lg',
											singleValue: () => 'text-gray-900',
											placeholder: () => 'text-gray-900',
											clearIndicator: () => 'text-gray-600',
											dropdownIndicator: () => 'text-gray-600',
										}}
									/>
								}
								<button
									className={classNames(
										'flex items-center w-32 md:w-40 border border-gray-300 rounded-lg px-2 h-10 relative text-[12px] xl:text-sm',
										jobToLoadReports.isLoading ? 'cursor-not-allowed bg-gray-100 text-gray-600' : 'cursor-pointer'
									)}
									onClick={() => {
										if (jobToLoadReports.isLoading) return;
										setCustomizeMenuOpen(true)
									}}
								>
									<span className='bp3-icon bp3-icon-edit absolute' />
									<span
										className='w-full text-center cursor-auto'
									>
										Customize
									</span>
								</button>
							</div>
						</div>

						{!jobToLoadReports.isLoading && reports?.length === 0 ? (
							<div className="flex flex-col justify-center h-64 text-center bg-gray-100">
								<h5 className="text-2xl font-thin text-gray-500 font-redhat-display">
									No Reports found..
								</h5>
							</div>
						) :
							<div className="overflow-x-scroll px-2 py-4">
								<ReportTableExpanded
									vesselId={vesselId}
									pageNumber={pageNumber}
									activeTab={activeTab}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
								/>
							</div>
						}
					</div>
					<Pagination
						{...paginationOfReports}
						onPageChange={function onPageChange(
							pageNumberFromPagination
						) {
							props.goToNewPage(
								vesselId || "",
								pageNumberFromPagination
							);
						}}
						isPageLoading={
							jobToLoadReports.isLoading ||
							jobToFilterReports.isLoading
						}
					/>
				</div>
				<ModalPortal
					isActive={customizeMenuOpen}
					onClose={() => setCustomizeMenuOpen(false)}
				>
					<div className={"bg-white rounded-md w-full"}>
						<CustomizeColumns
							onClose={() => {
								setCustomizeMenuOpen(false);
								props.setCustomColumns(selectedColumns);
							}}
							setColumns={props.setCustomColumns}
							selectedColumns={selectedColumns}
							allColumns={allCNRColumns}
							activeTab={activeTab}
							vesselType={vesselGenereicSubType}
							vesselFuelTypes={vesselFuelType.map((fuel: any) => fuel.value)}
							vesselId={vesselId}
						/>
					</div>
				</ModalPortal>
				{statusModalOpen ? (
					<StatusModal
						errorDetails={errorDetails}
						open={statusModalOpen}
						setOpen={setStatusModalOpen}
						retryReport={props.retryReport}
					/>
				) : null}
			</div>
		</>
	);
};

function mapStateToProps(store: IStore, ownProps: ReportsOwnProps) {
	const reportStore = store.app.logs;
	const onboardStore = store.app.vesselStore.onboard;
	const reports = logSelectors.getExpandedReports(reportStore);
	const selectedColumns = vesselOnboardSelectors.getCNRCustomColumns(onboardStore);
	const vesselStore = store.app.vesselStore;
	const vessel = vesselStore.vessels.byIds[ownProps.vesselId || ""] || {};

	return {
		reports: reports,
		paginationOfReports: logSelectors.getReportsPagination(reportStore),
		jobToLoadReports: logSelectors.jobToLoadExpandedReports(reportStore),
		jobToFilterReports: logSelectors.jobToLoadPrintTemplates(reportStore),
		selectedColumns,
		vesselSubType: vessel.vesselSubType || "",
		vesselFuelType: vessel.vesselFuels || [],
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadExpandedReports(
			pageNumber: number,
			showPageNumberInNotification: boolean = false,
			vesselId: string,
			reportTypes: string[],
		) {
			dispatch(
				logActions.commands.expandedReportsLoad({
					pageNumber,
					showPageNumberInNotification,
					vesselId,
					reportTypes,
				})
			);
		},
		goToNewPage(vesselId: string, pageNumber: number) {
			dispatch(
				historyActions.push(
					"#Report",
					rootRoutes.children.app.children.vessel.children.onboard.children.reports.url(
						vesselId,
						pageNumber
					)
				)
			);
		},
		setPageNumber(pageNumber: number) {
			dispatch(logActions.document.reportsPaginationCurrentPageSet(pageNumber));
		},
		resetPageNumber() {
			dispatch(logActions.document.reportsPaginationCurrentPageSet(1));
		},
		setCustomColumns(columns: any) {
			dispatch(vesselOnboardActions.document.cnrCustomColumnsSet(columns));
		},
		retryReport(reportType: EReportType, docId: string, vesselId: string) {
			dispatch(
				logActions.commands.retryReport(reportType, docId, vesselId)
			);
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Reports);